/**
 * GLSL shader library for psychedelic/dissociative phenomenology visualization.
 *
 * Each chunk implements a specific result from the cognitive-neuroscience
 * literature on altered states, translated into browser-grade WebGL. The
 * chunks are composed into ShaderMaterials by the scene components.
 *
 * Primary references:
 *  - Klüver (1926/1966): "Mescal and Mechanisms of Hallucinations" — the four
 *    invariant form constants (tunnel/funnel, spiral, lattice/honeycomb, cobweb).
 *  - Ermentrout & Cowan (1979); Bressloff et al. (2001): form constants are
 *    eigenmodes of hyperexcitable primary visual cortex (V1). V1's retinotopic
 *    map is approximately log-polar: visual (r,θ) ↦ cortical (u,v)=(log r, θ).
 *    A planar activity wave in cortex projects, through the inverse map, to a
 *    form constant in visual space. We render the cortical field and apply the
 *    inverse log-polar map — the geometry *is* the science.
 *  - Atasoy et al. (2017): connectome-harmonic decomposition shows energy
 *    shifts toward higher-curvature (hyperbolic) harmonics under psychedelics.
 *  - Lehar: hyperbolic geometry of DMT experience ("portal/tunnel").
 *  - Tangen et al.: symmetry/repetition as the engine of hallucination.
 *  - Carhart-Harris & Friston (2019) REBUS: relaxed top-down priors → rising
 *    bottom-up entropy / approach to criticality.
 *  - Dittrich (1998) 5D-ASC: dimensional structure of altered consciousness.
 */

// ── Log-polar V1 retinotopic mapping (Ermentrout-Cowan) ────────────
// Visual (x,y) → cortical (u,v) = (log r, θ). Neural activity patterns in
// this cortical coordinate space ARE the form constants under the inverse map.
export const CORTICAL_MAP = /* glsl */ `
  // Visual-space point → cortical coordinates.
  vec2 toCortical(vec2 p) {
    float r = length(p);
    float a = atan(p.y, p.x);
    return vec2(log(max(r, 1e-3)), a);
  }
`;

// ── Cortical activity field (superposition of V1 eigenmodes) ───────
// Each term is a plane wave in cortex; under the inverse log-polar map it
// becomes a distinct Klüver form constant. Mixing weights are audio-driven
// so the field morphs between tunnel / spiral / lattice / cobweb.
export const CORTICAL_FIELD = /* glsl */ `
  ${CORTICAL_MAP}
  // weights.x = tunnel, .y = spiral, .z = lattice, .w = cobweb
  float corticalField(vec2 p, float t, vec4 w) {
    vec2 uv = toCortical(p);
    float s = 0.0;
    // Tunnel: wave along log-r → concentric annuli (radial portal).
    s += w.x * sin(uv.x * 9.0 - t * 2.2);
    // Spiral: wave along (log-r + θ) → logarithmic spiral arms.
    s += w.y * sin((uv.x + uv.y) * 6.5 + t * 1.6);
    // Lattice: 2D product wave → honeycomb/hexagonal grid.
    s += w.z * sin(uv.x * 11.0 + t) * sin(uv.y * 11.0 - t * 0.7) * 0.9;
    // Cobweb: branching radial+tangential interference.
    s += w.w * sin(uv.x * 7.0 + cos(uv.y * 5.0 + t)) * 0.8;
    return s;
  }
`;

// ── N-fold kaleidoscopic symmetry (Tangen) ─────────────────────────
// Psychedelic visuals are runaway symmetry detection. Folding the angular
// coordinate into N wedges (then mirroring) injects the kaleidoscopic
// repetition characteristic of high-intensity states. N scales with dose.
export const SYMMETRY_FOLD = /* glsl */ `
  // Fold a 2D point into N-fold rotational + mirror symmetry.
  vec2 foldSymmetry(vec2 p, float n) {
    float a = atan(p.y, p.x);
    float r = length(p);
    float seg = 6.28318530718 / max(n, 1.0);
    a = mod(a, seg);
    a = abs(a - seg * 0.5); // mirror within each wedge
    return vec2(cos(a), sin(a)) * r;
  }
`;

// ── Hyperbolic Poincaré warp (Atasoy / Lehar) ──────────────────────
// In the Poincaré disk model distances inflate toward the boundary; mapping
// screen space through this gives the "infinite depth / portal" of DMT.
// k is the (negative) curvature: 0 = flat (sober), →1 = deep hyperbolic.
export const HYPERBOLIC_WARP = /* glsl */ `
  vec2 hyperbolicWarp(vec2 p, float k) {
    // Map to disk, apply conformal hyperbolic stretch, map back.
    vec2 q = p * (1.0 - k * 0.45);
    float r2 = dot(q, q);
    float denom = 1.0 - k * r2;
    denom = max(denom, 0.05);
    return q / denom * (1.0 + k * 0.3);
  }
`;

// ── Value noise + fbm with domain warping (criticality / REBUS) ────
// Domain-warping depth and octave count scale with dose: relaxed priors →
// rising entropy → more self-similar detail. (Inigo Quilez technique.)
export const NOISE_FBM = /* glsl */ `
  float vhash(vec2 p) {
    p = fract(p * vec2(123.34, 345.45));
    p += dot(p, p + 34.345);
    return fract(p.x * p.y);
  }
  float vnoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    float a = vhash(i);
    float b = vhash(i + vec2(1.0, 0.0));
    float c = vhash(i + vec2(0.0, 1.0));
    float d = vhash(i + vec2(1.0, 1.0));
    return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
  }
  // fractal Brownian motion; oct controlled by dose (entropy).
  float fbm(vec2 p, int oct) {
    float v = 0.0;
    float a = 0.5;
    mat2 rot = mat2(0.8, 0.6, -0.6, 0.8); // rotate per octave to reduce axis bias
    for (int i = 0; i < 8; i++) {
      if (i >= oct) break;
      v += a * vnoise(p);
      p = rot * p * 2.0 + 1.7;
      a *= 0.5;
    }
    return v;
  }
  // domain-warped fbm — organic "drifting/morphing" of surfaces.
  float warpedFbm(vec2 p, int oct, float warp) {
    vec2 q = vec2(fbm(p, oct), fbm(p + vec2(5.2, 1.3), oct));
    vec2 r = vec2(fbm(p + warp * q + vec2(1.7, 9.2), oct),
                  fbm(p + warp * q + vec2(8.3, 2.8), oct));
    return fbm(p + warp * r, oct);
  }
`;

// ── Signed distance fields + smooth-min blending ───────────────────
// SDFs allow infinite-LOD smooth morphing (the "wavy form" / entity): a
// sphere melts into a torus into a cube via polynomial smooth-union. This is
// the SOTA alternative to meshes for ultra-smooth organic geometry.
export const SDF_LIB = /* glsl */ `
  float sdSphere(vec3 p, float r) { return length(p) - r; }
  float sdBox(vec3 p, vec3 b) {
    vec3 q = abs(p) - b;
    return length(max(q, 0.0)) + min(max(q.x, max(q.y, q.z)), 0.0);
  }
  float sdTorus(vec3 p, vec2 t) {
    vec2 q = vec2(length(p.xz) - t.x, p.y);
    return length(q) - t.y;
  }
  float sdOctahedron(vec3 p, float s) {
    p = abs(p);
    return (p.x + p.y + p.z - s) * 0.57735027;
  }
  // polynomial smooth-min (k = blend width).
  float smin(float a, float b, float k) {
    float h = clamp(0.5 + 0.5 * (b - a) / k, 0.0, 1.0);
    return mix(b, a, h) - k * h * (1.0 - h);
  }
  float smax(float a, float b, float k) {
    return -smin(-a, -b, k);
  }
`;

// ── Audio texture sampling helpers ─────────────────────────────────
// The 1D DataTexture: R = band level, G = flux, B = centroid gradient,
// A = dissonance. Sample by perceptual (log) band index.
export const AUDIO_SAMPLE = /* glsl */ `
  // band01: 0..1 perceptual position across the spectrum.
  float sampleBand(sampler2D tex, float band01) {
    return texture2D(tex, vec2(clamp(band01, 0.0, 1.0), 0.5)).r;
  }
  float sampleFlux(sampler2D tex, float band01) {
    return texture2D(tex, vec2(clamp(band01, 0.0, 1.0), 0.5)).g / 255.0;
  }
  float sampleDissonance(sampler2D tex) {
    return texture2D(tex, vec2(0.5, 0.5)).a / 255.0;
  }
`;

// ── Psychedelic color mapping ──────────────────────────────────────
// HSL → RGB with HDR boost so bloom can lift bright bands above 1.0.
export const COLOR_LIB = /* glsl */ `
  vec3 hsl2rgb(vec3 c) {
    vec3 rgb = clamp(abs(mod(c.x * 6.0 + vec3(0.0, 4.0, 2.0), 6.0) - 3.0) - 1.0, 0.0, 1.0);
    return c.z + c.y * (rgb - 0.5) * (1.0 - abs(2.0 * c.z - 1.0));
  }
  // Hue rotates with the 5D-ASC synesthesia dimension (brightness); saturation
  // rises with restructuring; lightness shifts with vigilance.
  vec3 ascColor(float band01, float brightness, float restructuring, float vigilance, float time) {
    float hue = fract(band01 * 0.8 + brightness * 0.4 + time * 0.02);
    float sat = 0.7 + restructuring * 0.3;
    float lit = 0.45 + (1.0 - vigilance) * 0.25;
    return hsl2rgb(vec3(hue, sat, lit));
  }
`;
